import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { TablePagination } from "@/components/TablePagination/TablePagination";
import { TableEmptyRow } from "@/components/Table/TableEmptyRow";
import { initialPageSize } from "@/utils/preferredPageSize";

import { MetricHelp } from "./MetricHelp";
import { formatDate, formatWindow } from "../utils/featureUsage";

import type {
	ProductUsageFeatureRef,
	ProductUsageFreshness,
	ProductUsageCohort,
	ProductUsageDistinctUsersCoverage,
} from "@signalist/shared-types";

export function UsageStatus({
	isLoading,
	isError,
	data,
	retry,
}: {
	isLoading?: boolean;
	isError?: boolean;
	data?: { lastRolledUpDate: string | null };
	retry?: () => void;
}) {
	const { t } = useTranslation();

	if (isError) {
		return (
			<div role="alert" className="py-8 text-status-error">
				{t("featureUsage.error")}
				{retry && (
					<button className="ml-3 cursor-pointer underline" type="button" onClick={retry}>
						{t("featureUsage.retry")}
					</button>
				)}
			</div>
		);
	}

	if (isLoading || !data) {
		return (
			<div role="status" className="py-8 text-text-meta">
				{t("featureUsage.loading")}
			</div>
		);
	}

	if (data.lastRolledUpDate !== null) {
		return null;
	}

	return (
		<table className="w-full">
			<tbody>
				<TableEmptyRow
					colSpan={1}
					noDataText={
						<div role="status">
							<div>{t("featureUsage.empty")}</div>
							<p className="mt-3 text-text-meta">{t("featureUsage.capture")}</p>
							<p className="mt-2 text-xs text-text-meta">{t("featureUsage.rollup")}</p>
						</div>
					}
				/>
			</tbody>
		</table>
	);
}
export function UsageSummary({
	data,
}: {
	data: ProductUsageFreshness & {
		cohort?: ProductUsageCohort;
	} & Partial<ProductUsageDistinctUsersCoverage>;
}) {
	const { t } = useTranslation();

	return (
		<div className="my-4 space-y-2 text-xs text-text-meta">
			<div className="flex flex-wrap items-center gap-2">
				<span>{t("featureUsage.window", { range: formatWindow(data.window) })}</span>
				<MetricHelp
					label={t("featureUsage.metrics.window")}
					definition={t("featureUsage.help.window")}
				/>
				<span>{t("featureUsage.staffExcluded")}</span>
				{data.lastRolledUpDate && (
					<span>{t("featureUsage.rolledUp", { date: formatDate(data.lastRolledUpDate) })}</span>
				)}
			</div>
			{data.cohort && (
				<h2 className="text-sm font-semibold text-text">
					{data.cohort.description}
					<MetricHelp
						label={t("featureUsage.metrics.cohort")}
						definition={t("featureUsage.help.cohort")}
					/>
				</h2>
			)}
			{data.distinctUsersDates && (
				<div>
					{t("featureUsage.coverage", { count: data.distinctUsersDays })}
					<MetricHelp
						label={t("featureUsage.metrics.coverage")}
						definition={t("featureUsage.help.coverage", {
							dates: data.distinctUsersDates.join(", ") || t("featureUsage.notMeasured"),
						})}
					/>
				</div>
			)}
		</div>
	);
}
export function FeatureCell({ feature }: { feature: ProductUsageFeatureRef }) {
	return (
		<div>
			<div className="font-medium text-text">{feature.label}</div>
			<div className="mt-1 text-xs text-text-meta">
				{feature.area} · {feature.key}
			</div>
		</div>
	);
}
export interface UsageColumn<Row> {
	key: string;
	title: ReactNode;
	render: (row: Row) => ReactNode;
	sort?: () => void;
	direction?: "asc" | "desc";
}
export function UsageTable<Row extends ProductUsageFeatureRef>({
	rows,
	columns,
	label,
	resetKey,
}: {
	rows: readonly Row[];
	columns: UsageColumn<Row>[];
	label: string;
	resetKey?: string;
}) {
	const { t } = useTranslation();
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(() => initialPageSize({ fallback: 10 }));
	const [seenKey, setSeenKey] = useState(resetKey);

	if (seenKey !== resetKey) {
		setSeenKey(resetKey);
		setPage(1);
	}

	const count = rows.length;
	const pages = Math.max(1, Math.ceil(count / rowsPerPage));
	const current = Math.min(page, pages);
	const visible = rows.slice((current - 1) * rowsPerPage, current * rowsPerPage);

	return (
		<>
			<div className="overflow-x-auto">
				<table aria-label={label} className="w-full text-left text-sm">
					<thead>
						<tr>
							{columns.map((column) => (
								<th
									key={column.key}
									aria-sort={
										column.direction === "asc"
											? "ascending"
											: column.direction === "desc"
												? "descending"
												: undefined
									}
									className="border-b border-border-light px-3 py-3 font-normal text-text-meta"
								>
									{column.title}
									{column.sort && (
										<button
											aria-label={t("featureUsage.sort", {
												column: t(`featureUsage.metrics.${column.key}`),
											})}
											className="ml-1 cursor-pointer"
											type="button"
											onClick={column.sort}
										>
											↕
										</button>
									)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{count === 0 && (
							<TableEmptyRow colSpan={columns.length} noDataText={t("featureUsage.noMatches")} />
						)}
						{visible.map((row) => (
							<tr key={row.key}>
								{columns.map((column) => (
									<td key={column.key} className="border-b border-border-light px-3 py-3 align-top">
										{column.render(row)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{count > 0 && (
				<div className="mt-3 flex justify-end">
					<TablePagination
						currentPage={current}
						handleChangeRowsPerPage={(size) => {
							setRowsPerPage(size);
							setPage(1);
						}}
						handleNavigateToPage={setPage}
						limit={rowsPerPage}
						maxPage={pages}
					/>
				</div>
			)}
		</>
	);
}
